import React from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    BlinkingDot,
    MonoLabel,
    FieldButton,
} from "@/components/proof/primitives";
import { easing } from "@/lib/animations";
import { JOURNAL, getJournalBySlug } from "@/data/journal";

const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const Block = ({ block, idx }) => {
    if (block.type === "h2") {
        return (
            <motion.h2
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.7, ease: easing.primary }}
                style={{
                    margin: "clamp(28px, 3vw, 48px) 0 8px",
                    fontFamily: "var(--font-display)",
                    fontWeight: 700,
                    fontSize: "clamp(22px, 2.3vw, 34px)",
                    letterSpacing: "-0.025em",
                    textTransform: "uppercase",
                    color: "var(--on-ink)",
                }}
            >
                {block.text}
            </motion.h2>
        );
    }
    if (block.type === "pull") {
        return (
            <motion.blockquote
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.9, ease: easing.primary }}
                style={{
                    margin: "clamp(32px, 4vw, 56px) 0",
                    padding: "0 clamp(20px, 3vw, 40px)",
                    borderLeft: "2px solid var(--signal)",
                    fontFamily: "var(--font-serif)",
                    fontStyle: "italic",
                    fontSize: "clamp(22px, 2.6vw, 36px)",
                    lineHeight: 1.35,
                    color: "var(--on-ink)",
                    maxWidth: "30ch",
                }}
            >
                {block.text}
            </motion.blockquote>
        );
    }
    return (
        <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: easing.primary }}
            style={{
                margin: "0 0 18px",
                fontFamily: "var(--font-body)",
                fontSize: "clamp(15px, 1.1vw, 17px)",
                lineHeight: 1.7,
                color: "var(--on-ink-soft)",
                maxWidth: "62ch",
            }}
        >
            {block.text}
        </motion.p>
    );
};

export default function JournalPost() {
    const { slug } = useParams();
    const post = getJournalBySlug(slug);

    if (!post) {
        return (
            <section
                style={{
                    minHeight: "100vh",
                    background: "var(--ink)",
                    color: "var(--on-ink)",
                    padding: "clamp(120px, 14vw, 200px) clamp(24px, 5vw, 80px)",
                }}
                data-testid="page-journal-notfound"
            >
                <MonoLabel tone="mute">Signal lost</MonoLabel>
                <h1
                    style={{
                        fontFamily: "var(--font-display)",
                        fontWeight: 700,
                        fontSize: "clamp(36px, 6vw, 72px)",
                        letterSpacing: "-0.04em",
                        textTransform: "uppercase",
                        margin: "16px 0 28px",
                    }}
                >
                    Entry not found.
                </h1>
                <FieldButton to="/journal" data-testid="journal-notfound-back">
                    Return to Journal
                </FieldButton>
            </section>
        );
    }

    const idx = JOURNAL.findIndex((j) => j.slug === slug);
    const prev = idx > 0 ? JOURNAL[idx - 1] : null;
    const next = idx < JOURNAL.length - 1 ? JOURNAL[idx + 1] : null;

    return (
        <article
            data-testid={`page-journal-${post.slug}`}
            style={{
                position: "relative",
                background: "var(--ink)",
                color: "var(--on-ink)",
                minHeight: "100vh",
                padding:
                    "clamp(120px, 14vw, 200px) clamp(24px, 5vw, 80px) clamp(80px, 10vw, 160px)",
            }}
            className="proof-page-shell"
        >
            {/* META */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 18,
                    marginBottom: "clamp(36px, 5vw, 72px)",
                }}
            >
                <MonoLabel
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 12,
                    }}
                >
                    <BlinkingDot />
                    Entry [{String(idx + 1).padStart(2, "0")}]
                </MonoLabel>
                <MonoLabel
                    tone="mute"
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 12,
                    }}
                >
                    <Link
                        to="/"
                        style={{ color: "inherit", textDecoration: "none" }}
                    >
                        Proof
                    </Link>
                    <span aria-hidden="true">/</span>
                    <Link
                        to="/journal"
                        style={{ color: "inherit", textDecoration: "none" }}
                    >
                        Journal
                    </Link>
                </MonoLabel>
            </div>

            {/* TITLE + META */}
            <header
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                    marginBottom: "clamp(36px, 5vw, 72px)",
                    maxWidth: "20ch",
                }}
            >
                <MonoLabel tone="mute" data-testid="journal-meta">
                    {formatDate(post.publishedAt)} · {post.author} ·{" "}
                    {post.readMinutes} min
                </MonoLabel>
                <motion.h1
                    initial={{ opacity: 0, y: 28 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.95, ease: easing.primary }}
                    data-testid="journal-title"
                    style={{
                        margin: 0,
                        fontFamily: "var(--font-display)",
                        fontWeight: 700,
                        fontSize: "clamp(40px, 7vw, 112px)",
                        letterSpacing: "-0.04em",
                        lineHeight: 0.95,
                        textTransform: "uppercase",
                    }}
                >
                    {post.title}
                </motion.h1>
            </header>

            {/* COVER */}
            <motion.div
                initial={{ opacity: 0, scale: 1.02 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.95, delay: 0.2, ease: easing.primary }}
                style={{
                    aspectRatio: "16 / 9",
                    background: post.bgTone,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "clamp(40px, 5vw, 80px)",
                    overflow: "hidden",
                }}
                data-testid="journal-cover"
            >
                <img
                    src={post.cover}
                    alt={post.title}
                    draggable={false}
                    style={{
                        width: "55%",
                        height: "70%",
                        objectFit: "contain",
                        mixBlendMode: "multiply",
                    }}
                />
            </motion.div>

            {/* BODY */}
            <div
                style={{
                    maxWidth: "62ch",
                    margin: "0 auto 0 0",
                }}
                data-testid="journal-body"
            >
                {post.body.map((b, i) => (
                    <Block key={i} block={b} idx={i} />
                ))}
            </div>

            {/* TAGS */}
            {post.tags?.length > 0 && (
                <div
                    style={{
                        marginTop: "clamp(40px, 5vw, 80px)",
                        paddingTop: 22,
                        borderTop: "1px solid var(--on-ink-line)",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 10,
                    }}
                >
                    <MonoLabel tone="mute" style={{ marginRight: 6 }}>
                        Tagged
                    </MonoLabel>
                    {post.tags.map((t) => (
                        <MonoLabel
                            key={t}
                            tone="default"
                            style={{
                                padding: "6px 14px",
                                border: "1px solid var(--on-ink-line-strong, rgba(255,255,255,0.35))",
                            }}
                        >
                            {t}
                        </MonoLabel>
                    ))}
                </div>
            )}

            {/* PREV / NEXT */}
            <nav
                aria-label="Journal navigation"
                style={{
                    marginTop: "clamp(48px, 6vw, 96px)",
                    paddingTop: 22,
                    borderTop: "1px solid var(--on-ink-line)",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 24,
                }}
                className="proof-grid-equal"
                data-testid="journal-pager"
            >
                {prev ? (
                    <Link
                        to={`/journal/${prev.slug}`}
                        data-testid="journal-prev"
                        style={{
                            color: "var(--on-ink-soft)",
                            textDecoration: "none",
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                        }}
                    >
                        <MonoLabel tone="mute">← Previous</MonoLabel>
                        <span
                            style={{
                                fontFamily: "var(--font-display)",
                                fontWeight: 600,
                                fontSize: "clamp(16px, 1.4vw, 22px)",
                                letterSpacing: "-0.015em",
                                textTransform: "uppercase",
                                color: "var(--on-ink)",
                            }}
                        >
                            {prev.title}
                        </span>
                    </Link>
                ) : <span />}
                {next ? (
                    <Link
                        to={`/journal/${next.slug}`}
                        data-testid="journal-next"
                        style={{
                            color: "var(--on-ink-soft)",
                            textDecoration: "none",
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                            textAlign: "right",
                            alignItems: "flex-end",
                        }}
                    >
                        <MonoLabel tone="mute">Next →</MonoLabel>
                        <span
                            style={{
                                fontFamily: "var(--font-display)",
                                fontWeight: 600,
                                fontSize: "clamp(16px, 1.4vw, 22px)",
                                letterSpacing: "-0.015em",
                                textTransform: "uppercase",
                                color: "var(--on-ink)",
                            }}
                        >
                            {next.title}
                        </span>
                    </Link>
                ) : <span />}
            </nav>
        </article>
    );
}
